<script setup>
// BonBoZ: Zalo KHÔNG phải kênh gốc của Chatwoot.
//
// Nó chạy qua sidecar `zca-bridge`: bridge giữ phiên Zalo, Chatwoot chỉ là một
// kênh API mà bridge đẩy tin vào. Trước màn này, người dùng phải tự bê ba thứ
// qua lại giữa hai hệ thống — dán webhook URL kèm secret vào Chatwoot, rồi chép
// ngược `inbox_identifier` và `Inbox ID` sang trang admin của bridge. Sai một ký
// tự là im lặng không chạy.
//
// Màn này làm hết phần bê vác đó. Riêng bước QUÉT QR thì không chuyển vào đây
// được — phiên đăng nhập Zalo nằm ở bridge, Chatwoot không giữ được nó. Nên
// bước cuối là một nút mở sang trang admin của bridge.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import ZaloAPI from 'dashboard/api/bonboz/zalo';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const tenHopThu = ref('Zalo');
const dangTao = ref(false);
const ketQua = ref(null);

const taoHopThu = async () => {
  dangTao.value = true;
  try {
    const { data } = await ZaloAPI.taoHopThu(tenHopThu.value.trim() || 'Zalo');
    ketQua.value = data;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('BONBOZ.ZALO.LOI_TAO'));
  } finally {
    dangTao.value = false;
  }
};

const chep = async giaTri => {
  await copyTextToClipboard(giaTri);
  useAlert(t('BONBOZ.ZALO.DA_CHEP'));
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('BONBOZ.ZALO.TIEU_DE')"
      :header-content="$t('BONBOZ.ZALO.MO_TA')"
    />

    <!-- Bước 1: đặt tên rồi tạo. -->
    <form
      v-if="!ketQua"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="taoHopThu"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('BONBOZ.ZALO.TEN.NHAN') }}
          <input
            v-model="tenHopThu"
            type="text"
            :placeholder="$t('BONBOZ.ZALO.TEN.GOI_Y')"
          />
        </label>
        <p class="help-text">{{ $t('BONBOZ.ZALO.TEN.CHU_THICH') }}</p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="dangTao"
          type="submit"
          solid
          blue
          :label="$t('BONBOZ.ZALO.NUT_TAO')"
        />
      </div>
    </form>

    <!-- Bước 2: đã tạo xong, còn mỗi việc quét QR bên bridge. -->
    <div v-else class="flex flex-col gap-4 max-w-2xl">
      <div
        class="flex items-start gap-2 p-4 rounded-lg bg-n-teal-2 text-n-slate-12"
      >
        <span class="i-lucide-check-circle-2 size-5 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium m-0">
            {{ $t('BONBOZ.ZALO.XONG.TIEU_DE', { ten: ketQua.name }) }}
          </p>
          <p class="text-sm text-n-slate-11 m-0 mt-1">
            {{ $t('BONBOZ.ZALO.XONG.MO_TA') }}
          </p>
        </div>
      </div>

      <!-- Hai giá trị này phải gõ sang bridge. Cho nút chép để khỏi gõ tay:
           sai một ký tự là hội thoại Zalo rơi vào hộp thư khác. -->
      <div class="flex flex-col gap-3">
        <div
          v-for="o in [
            {
              nhan: $t('BONBOZ.ZALO.XONG.INBOX_ID'),
              giaTri: String(ketQua.id),
            },
            {
              nhan: $t('BONBOZ.ZALO.XONG.DINH_DANH'),
              giaTri: ketQua.inbox_identifier,
            },
          ]"
          :key="o.nhan"
          class="flex items-center justify-between gap-3 px-4 py-3 border rounded-lg border-n-strong"
        >
          <div class="min-w-0">
            <p class="text-xs text-n-slate-11 m-0">{{ o.nhan }}</p>
            <p class="font-mono text-sm text-n-slate-12 m-0 truncate">
              {{ o.giaTri }}
            </p>
          </div>
          <NextButton
            faded
            slate
            size="sm"
            icon="i-lucide-copy"
            :label="$t('BONBOZ.ZALO.XONG.CHEP')"
            @click="chep(o.giaTri)"
          />
        </div>
      </div>

      <div class="flex items-center gap-3 mt-2">
        <a
          v-if="ketQua.admin_url"
          :href="ketQua.admin_url"
          target="_blank"
          rel="noopener noreferrer"
        >
          <NextButton
            solid
            blue
            icon="i-lucide-qr-code"
            :label="$t('BONBOZ.ZALO.XONG.MO_BRIDGE')"
          />
        </a>
        <p v-else class="text-sm text-n-ruby-11 m-0">
          {{ $t('BONBOZ.ZALO.XONG.THIEU_ADMIN_URL') }}
        </p>
      </div>

      <p class="text-sm text-n-slate-11 m-0">
        {{ $t('BONBOZ.ZALO.XONG.CANH_BAO_ACC_PHU') }}
      </p>
    </div>
  </div>
</template>
